import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageBody, SectionLabel, Spinner } from '@wundo/ui';
import { PageHeader } from '@/components/PageHeader.tsx';
import { DateRangeFilter } from '@/components/DateRangeFilter.tsx';
import type { DateRangeOption } from '@/components/dateRangeOptions.ts';
import { MeetingDocumentFunnelCard } from '@/components/dashboard/MeetingDocumentFunnelCard.tsx';
import { MetricsHeadline } from '@/components/dashboard/MetricsHeadline.tsx';
import { MetricChartCard } from '@/components/dashboard/MetricChartCard.tsx';
import { NudgeBreakdownTable } from '@/components/dashboard/NudgeBreakdownTable.tsx';
import { NudgeFunnelCard } from '@/components/dashboard/NudgeFunnelCard.tsx';
import { RecordingsCard } from '@/components/dashboard/RecordingsCard.tsx';
import { RoleplayFunnelCard } from '@/components/dashboard/RoleplayFunnelCard.tsx';
import { SessionDistributionCard } from '@/components/dashboard/SessionDistributionCard.tsx';
import { METRIC_INFO } from '@/components/dashboard/metricsInfo.ts';
import { useMetricsHistory } from '@/components/dashboard/useMetricsHistory.ts';
import { useOrganisationFilter } from '@/lib/activeOrganisation.ts';
import { requireInternalOrganisation } from '@/lib/staffGate.ts';

/* This page was `requiresSuperadminInternal` in the Vue router. */
export const Route = createFileRoute('/_app/dashboard/metrics-history')({
  staticData: { orgScope: 'optional' },
  beforeLoad: ({ context }) => requireInternalOrganisation(context.queryClient),
  component: MetricsHistoryPage,
});

/* Snapshots are daily and the newest is yesterday's, so a day is the floor. */
const RANGE_OPTIONS: readonly DateRangeOption[] = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
];

const DEFAULT_RANGE = '30d';

export function MetricsHistoryPage() {
  const organisation = useOrganisationFilter();
  const [range, setRange] = useState<string>(DEFAULT_RANGE);

  const option = RANGE_OPTIONS.find((one) => one.value === range) ?? RANGE_OPTIONS[1];
  const { stats, points, loading } = useMetricsHistory(
    option.days ?? 30,
    organisation?.id,
  );
  const activation = stats?.activation;
  const scopeLabel = organisation?.name ?? 'All organisations';

  return (
    <>
      <PageHeader
        title='Metrics History'
        subtitle={`${scopeLabel} — ${option.label} through yesterday`}
        filters={
          <DateRangeFilter options={RANGE_OPTIONS} value={range} onChange={setRange} />
        }
      />

      <PageBody>
        {loading ? (
          <p className='flex items-center gap-2 text-sm text-ink-muted'>
            <Spinner />
            Loading metrics…
          </p>
        ) : (
          <div className='flex flex-col gap-6'>
            <MetricsHeadline stats={stats} />

            <div className='grid grid-cols-2 gap-6 max-agenda:grid-cols-1'>
              <MetricChartCard
                title='Weekly Active Users'
                dataPoints={points('wau')}
                percentOf={activation?.total}
                description='Sent a message, opened the app or let the notetaker in during the last 7 days, as % of all provisioned users'
                retentionOf={activation?.activated}
                info={METRIC_INFO.activeUsers}
              />
              <MetricChartCard
                title='Monthly Active Users'
                dataPoints={points('mau')}
                percentOf={activation?.total}
                description='Sent a message, opened the app or let the notetaker in during the last 30 days, as % of all provisioned users'
                retentionOf={activation?.activated}
                info={METRIC_INFO.activeUsers}
              />
              <SessionDistributionCard
                distribution={stats?.sessions.distribution ?? []}
                average={stats?.sessions.average_per_messaging_user ?? null}
              />
              <MetricChartCard
                title='Coaching Questions'
                dataPoints={points('coaching_questions')}
              />
              <RoleplayFunnelCard
                received={points('roleplay_received')}
                clicked={points('roleplay_clicked')}
                voiceStarted={points('voice_roleplay_started')}
                chatStarted={points('chat_roleplay_started')}
                voiceFinished={points('voice_roleplay_finished')}
                chatFinished={points('chat_roleplay_finished')}
              />
              <NudgeFunnelCard
                shown={points('nudge_shown')}
                clicked={points('nudge_clicked')}
                dismissed={points('nudge_dismissed')}
                completed={points('nudge_completed')}
              />
              <div className='col-span-2 max-agenda:col-span-1'>
                <NudgeBreakdownTable rows={stats?.nudges ?? []} />
              </div>
            </div>

            <SectionLabel as='h2'>Meetings</SectionLabel>
            <div className='grid grid-cols-2 gap-6 max-agenda:grid-cols-1'>
              <MeetingDocumentFunnelCard
                title='Preparation funnel'
                madeLabel='Generated'
                made={points('prep_generated')}
                opened={points('prep_opened')}
                info={METRIC_INFO.prepFunnel}
              />
              <MeetingDocumentFunnelCard
                title='Report funnel'
                madeLabel='Created'
                made={points('report_created')}
                opened={points('report_opened')}
                info={METRIC_INFO.reportFunnel}
              />
              <RecordingsCard
                bot={points('recording_done_bot')}
                physical={points('recording_done_microphone')}
                info={METRIC_INFO.recordings}
              />
              <MetricChartCard
                title='Meeting users'
                dataPoints={points('meeting_users')}
                percentOf={activation?.total}
                description='Opened a preparation or report, or had a recording finish, in the last 7 days'
                info={METRIC_INFO.meetingUsers}
              />
            </div>
          </div>
        )}
      </PageBody>
    </>
  );
}
